import { useState } from 'react';
import type { FocusEvent } from 'react';
import { Box, TextLabel } from '@customerio/pluma-components/react';
import { CODE_LENGTH } from './verification-code';
import * as styles from './verification-code-input.css';

const INPUT_ID = 'verification-code-input';
const ERROR_ID = 'verification-code-error';

type VerificationCodeInputProps = {
  value: string;
  onValueChange: (value: string) => void;
  /** Shown below the boxes; the caller decides when an error is surfaced. */
  error?: string | null;
};

/**
 * Six-box code entry: one text input, letter-spaced over an SVG of six rects,
 * with the box at the caret highlighted. A single input keeps paste, autofill
 * and backspace behaving natively — six separate inputs would not.
 */
export function VerificationCodeInput({ value, onValueChange, error }: VerificationCodeInputProps) {
  const [isFocused, setIsFocused] = useState(false);

  const activeBoxIndex = isFocused ? Math.min(value.length, CODE_LENGTH - 1) : -1;
  const hasError = error != null && error !== '';

  const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
    // Browsers place the caret after the focus event resolves (on click, at the
    // clicked position), so a synchronous move here would be overridden.
    const target = event.target;
    setTimeout(() => {
      target.setSelectionRange(target.value.length, target.value.length);
    }, 0);
    setIsFocused(true);
  };

  return (
    <Box className={styles.section}>
      <TextLabel htmlFor={INPUT_ID}>Verification code</TextLabel>
      <Box className={styles.inputWrapper}>
        <svg
          className={styles.svgElement}
          width={styles.OTP_TOTAL_WIDTH}
          height={styles.OTP_BOX_HEIGHT}
          viewBox={styles.OTP_VIEWBOX}
          xmlns="http://www.w3.org/2000/svg"
        >
          {styles.OTP_RECTS.map((box, index) => (
            <rect
              key={box.x}
              className={`${styles.svgRect} ${index === activeBoxIndex ? styles.svgRectActive : ''}`}
              x={box.x}
              y={box.y}
              width={box.width}
              height={box.height}
              rx={box.rx}
              fill="none"
            />
          ))}
        </svg>
        {/* oxlint-disable-next-line pluma/enforce-pluma-elements -- TextField renders its own
            border, padding and background; the six boxes are an SVG behind a transparent,
            letter-spaced input, which that chrome would cover. */}
        <input
          id={INPUT_ID}
          className={`${styles.input} fs-mask`}
          data-test-signup-form-input="verification_code"
          autoComplete="one-time-code"
          inputMode="numeric"
          maxLength={CODE_LENGTH}
          type="text"
          value={value}
          // Standing in for what Pluma's TextField would wire up: without these
          // the message below is invisible to a screen reader.
          aria-invalid={hasError}
          aria-describedby={hasError ? ERROR_ID : undefined}
          onInput={(event) => onValueChange(event.currentTarget.value.trim())}
          onFocus={handleFocus}
          onBlur={() => setIsFocused(false)}
          autoFocus
        />
      </Box>
      {hasError ? (
        <span id={ERROR_ID} className={styles.errorText}>
          {error}
        </span>
      ) : null}
    </Box>
  );
}
